import { useEffect } from "react";

import { getEmbedConfig } from "@/lib/embed/config";
import { applyAccent } from "@/lib/embed/theme";
import {
  buildReady,
  buildResize,
  isInboundMessage,
  OutboundMessage,
} from "@/lib/embed/messages";

/**
 * Wire up the origin- and schema-checked postMessage bridge between the
 * embedded app and its host portal.
 *
 * Inbound (parent -> child): only messages whose origin matches
 * `allowedParentOrigin` AND whose source is `window.parent` AND that pass the
 * schema guard are handled; everything else is ignored silently.
 *
 * Outbound (child -> parent): `ready` on mount, `resize` on content-height
 * change. Always posted with an explicit `targetOrigin` — never "*". If no
 * allowed origin is configured, the bridge is fully disabled (fail-closed):
 * no listener, no observer, no posts.
 *
 * @param rootRef ref to the EmbedLayout root element to observe for resizes.
 */
export function useEmbedBridge(
  rootRef: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const { allowedParentOrigin } = getEmbedConfig();

    // Fail-closed: without a configured parent origin we neither send nor
    // accept anything.
    if (!allowedParentOrigin) {
      return;
    }

    const post = (message: OutboundMessage) => {
      window.parent.postMessage(message, allowedParentOrigin);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== allowedParentOrigin) return;
      if (event.source !== window.parent) return;
      if (!isInboundMessage(event.data)) return;

      switch (event.data.type) {
        case "theme":
          applyAccent(event.data.payload.accent);
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    // Announce readiness to the parent.
    post(buildReady());

    // Report content height changes so the parent can size the iframe.
    let lastHeight = -1;
    const observer = new ResizeObserver(() => {
      const node = rootRef.current;
      if (!node) return;
      const height = node.scrollHeight;
      if (height <= 0 || height === lastHeight) return;
      lastHeight = height;
      post(buildResize(height));
    });

    if (rootRef.current) {
      observer.observe(rootRef.current);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
      observer.disconnect();
    };
  }, [rootRef]);
}
